import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { SafePipe } from '../../pipes/safe.pipe';
import { documentStatus, documentPaymentStatus } from '../../services/statusList';
import { isLifetimeMemberType, LIFETIME_MEMBER_LABEL } from '../../utils/membership.util';

@Component({
  selector: 'app-request-renew-new-member-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StaffHeaderComponent,
    StaffNavComponent,
    SafePipe
  ],
  templateUrl: './request-renew-new-member-form.component.html',
  styleUrl: './request-renew-new-member-form.component.scss'
})
export class RequestRenewNewMemberFormComponent implements OnInit, OnDestroy {
  requestId: string = ''; // Request ID from route params
  userId: string = ''; // User ID from request data

  renewalForm!: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  isSubmitting = false;
  isLoading = false;
  error: string = '';

  // Original data for comparison
  originalData: any = null;

  // Documents from API
  documents: Array<{ name: string, file: string }> = [];

  // Payment slip URL from API
  paymentSlip: string = '';

  // Receipt code
  existingCodeReceipt: string = '';

  // CPD Hours data from API
  cpdHoursData: any[] = [];

  // Dynamic arrays for display
  valuationExperience: any[] = [];
  educationHistory: any[] = [];
  specialCourses: any[] = [];

  private subscription = new Subscription();

  // Dropdown options
  educationLevels = [
    { value: '1', label: 'ต่ำกว่า ปวส.' },
    { value: '2', label: 'ปวส.' },
    { value: '3', label: 'ระดับปริญญาตรี' },
    { value: '4', label: 'ระดับปริญญาโท' },
    { value: '5', label: 'ระดับปริญญาเอก' },
    { value: '6', label: 'วุฒิการศึกษาอื่นๆ' }
  ];

  membershipTypes = [
    { value: 'วิสามัญ', label: 'วิสามัญ (ค่าธรรมเนียม 500 บาทต่อปี)', fee: 500, isLifetime: false },
    { value: 'สามัญ', label: 'สามัญ (ค่าธรรมเนียม 500 บาทต่อปี)', fee: 500, isLifetime: false },
    { value: 'ตลอดชีพ', label: 'ตลอดชีพ (ค่าธรรมเนียม 10,000 บาทตลอดชีพ)', fee: 10000, isLifetime: true }
  ];

  // Use status lists from statusList.ts
  statusOptions = documentStatus;
  paymentStatusOptions = documentPaymentStatus;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Get requestId from route params
    this.route.params.subscribe(params => {
      this.requestId = params['requestId'];

      if (this.requestId) {
        this.loadRequestData();
      } else {
        this.error = 'ไม่พบข้อมูล Request ID';
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  /** ประเภทการต่ออายุของคำขอนี้ (รายปี / ตลอดชีพ) */
  membershipType: string = '';

  /** คำขอต่ออายุแบบตลอดชีพหรือไม่ */
  get isLifetimeRenewal(): boolean {
    return isLifetimeMemberType(this.membershipType);
  }

  /** ข้อความระยะเวลาที่ขอต่ออายุ */
  get renewPeriodText(): string {
    if (this.isLifetimeRenewal) {
      return LIFETIME_MEMBER_LABEL;
    }

    return `${this.renewalForm.get('workExperienceYears')?.value || 0} ปี`;
  }

  private initForm(): void {
    this.renewalForm = this.fb.group({
      // Request Information (Read-Only in Step 1)
      workExperienceYears: [{ value: '', disabled: true }, [Validators.required, Validators.min(0)]],
      feeAmount: [{ value: '', disabled: true }],
      status: [documentStatus[0], Validators.required],
      paymentStatus: [documentPaymentStatus[0], Validators.required],
      cardNumber: [''],
      cardExpiryDate: [''],

      // User Information (Read-Only)
      titleName: [{ value: '', disabled: true }],
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      idCard: [{ value: '', disabled: true }],
      dateOfBirth: [{ value: '', disabled: true }],
      nationality: [{ value: '', disabled: true }],
      phoneNumber: [{ value: '', disabled: true }],
      lineId: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }],

      // Current Address (Read-Only)
      currentHouseNumber: [{ value: '', disabled: true }],
      currentVillage: [{ value: '', disabled: true }],
      currentSoi: [{ value: '', disabled: true }],
      currentRoad: [{ value: '', disabled: true }],
      currentSubdistrict: [{ value: '', disabled: true }],
      currentDistrict: [{ value: '', disabled: true }],
      currentProvince: [{ value: '', disabled: true }],
      currentPostalCode: [{ value: '', disabled: true }],

      // Registered Address (Read-Only)
      registeredHouseNumber: [{ value: '', disabled: true }],
      registeredVillage: [{ value: '', disabled: true }],
      registeredSoi: [{ value: '', disabled: true }],
      registeredRoad: [{ value: '', disabled: true }],
      registeredSubdistrict: [{ value: '', disabled: true }],
      registeredDistrict: [{ value: '', disabled: true }],
      registeredProvince: [{ value: '', disabled: true }],
      registeredPostalCode: [{ value: '', disabled: true }],

      // Additional fields
      requestId: [''],
      createdAt: [''],
      updatedAt: ['']
    });
  }

  /**
   * Load request data from API - RENEWAL ENDPOINT
   */
  private loadRequestData(): void {
    this.error = '';

    // Use the RENEWAL API endpoint
    const apiUrl = `${environment.apiUrl}/form-renew-member/${this.requestId}`;

    console.log('🔍 Loading renewal request data from:', apiUrl);

    this.http.get(apiUrl).subscribe({
      next: (response: any) => {
        console.log('✅ Renewal request data loaded:', response);
        this.originalData = response;

        // Load documents if available
        if (response.documents && Array.isArray(response.documents)) {
          this.documents = response.documents;
          console.log('📄 Documents loaded:', this.documents);
        }

        // Load payment slip if available
        if (response.paymentSlip) {
          this.paymentSlip = response.paymentSlip;
          console.log('💳 Payment slip loaded:', this.paymentSlip);
        }

        // Extract userId and load user information
        if (response.userId) {
          this.loadUserInformation(response.userId, response);
        } else {
          console.warn('⚠️ No userId found in request data');
          this.populateForm(response);
        }
      },
      error: (error) => {
        console.error('❌ Error loading renewal request data:', error);
        this.error = error.error?.message || 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง';
      }
    });
  }

  /**
   * Load user information from user-information API
   */
  private loadUserInformation(userId: string, requestData: any): void {
    const userApiUrl = `${environment.apiUrl}/user-information/user/${userId}`;

    // Store userId for later use
    this.userId = userId;

    console.log('🔍 Loading user information from:', userApiUrl);

    this.http.get(userApiUrl).subscribe({
      next: (userInfo: any) => {
        console.log('✅ User information loaded:', userInfo);

        // Store user information in originalData for dynamic arrays access
        if (!this.originalData.user) {
          this.originalData.user = {};
        }
        this.originalData.user.userInformation = userInfo;

        // Populate dynamic arrays directly
        this.valuationExperience = userInfo.valuationExperience || [];
        this.educationHistory = userInfo.educationHistory || [];
        this.specialCourses = userInfo.specialCourses || [];

        console.log('📊 Dynamic arrays populated:', {
          valuationExperience: this.valuationExperience.length,
          educationHistory: this.educationHistory.length,
          specialCourses: this.specialCourses.length
        });

        // Load CPD hours data
        this.loadCpdHours(userId);

        // Merge request data with user information
        this.populateForm(requestData, userInfo);
      },
      error: (error) => {
        console.error('❌ Error loading user information:', error);
        console.warn('⚠️ Falling back to request data only');
        // Fallback to request data only
        this.populateForm(requestData);
      }
    });
  }

  /**
   * Load CPD hours data from API
   */
  private loadCpdHours(userId: string): void {
    const cpdApiUrl = `${environment.apiUrl}/cpd-hours/user/${userId}`;

    console.log('🔍 Loading CPD hours from:', cpdApiUrl);

    this.http.get(cpdApiUrl).subscribe({
      next: (response: any) => {
        console.log('✅ CPD hours loaded:', response);
        this.cpdHoursData = Array.isArray(response) ? response : [];
        console.log('📊 CPD hours count:', this.cpdHoursData.length);
      },
      error: (error) => {
        console.error('❌ Error loading CPD hours:', error);
        this.cpdHoursData = [];
      }
    });
  }

  /**
   * Populate form with API data - RENEWAL DATA STRUCTURE
   */
  private populateForm(requestData: any, userInfo?: any): void {
    console.log('📝 Populating form with renewal request data:', requestData);
    console.log('📝 Populating form with user info:', userInfo);

    try {
      // Store existing receipt code if present
      this.existingCodeReceipt = requestData.codeReceipt || '';

      // Populate renewal request fields (from form-renew-member API)
      this.membershipType = requestData.membershipType || '';

      this.renewalForm.patchValue({
        workExperienceYears: requestData.workExperienceYears || 0,
        feeAmount: requestData.feeAmount || 0,
        status: requestData.status || documentStatus[0],
        paymentStatus: requestData.paymentStatus || documentPaymentStatus[0],
        requestId: requestData.id || '',
        createdAt: requestData.createdAt || '',
        updatedAt: requestData.updatedAt || ''
      }, { emitEvent: false });

      // Populate user information from user-information API if available
      if (userInfo) {
        this.renewalForm.patchValue({
          // Personal Information
          firstName: userInfo.firstNameThai || userInfo.firstName || '',
          lastName: userInfo.lastNameThai || userInfo.lastName || '',
          idCard: userInfo.user?.cid || requestData.user?.cid || '',
          dateOfBirth: userInfo.dateOfBirth || '',
          nationality: userInfo.nationality || '',
          phoneNumber: userInfo.phoneNumber || userInfo.currentPhone || '',
          lineId: userInfo.currentLineId || '',
          email: userInfo.user?.email || requestData.user?.email || '',

          // Current Address - Map from flat structure
          currentHouseNumber: userInfo.currentAddress || userInfo.address || '',
          currentSubdistrict: userInfo.currentSubdistrict || userInfo.subdistrict || '',
          currentDistrict: userInfo.currentDistrict || userInfo.district || '',
          currentProvince: userInfo.currentProvince || userInfo.province || '',
          currentPostalCode: userInfo.currentPostalCode || userInfo.postalCode || '',

          // Registered Address - Use same as current if not provided separately
          registeredHouseNumber: userInfo.address || userInfo.currentAddress || '',
          registeredSubdistrict: userInfo.subdistrict || userInfo.currentSubdistrict || '',
          registeredDistrict: userInfo.district || userInfo.currentDistrict || '',
          registeredProvince: userInfo.province || userInfo.currentProvince || '',
          registeredPostalCode: userInfo.postalCode || userInfo.currentPostalCode || '',

          // VAT Member Information (editable fields)
          cardNumber: userInfo.vatId || '',
          cardExpiryDate: userInfo.vatExpired || ''
        }, { emitEvent: false });

        console.log('✅ Form populated successfully with user information from user-information API');
      }
      // Fallback to old structure if userInfo not provided
      else if (requestData.user && requestData.user.userInformation) {
        const oldUserInfo = requestData.user.userInformation;

        this.renewalForm.patchValue({
          titleName: oldUserInfo.titleName || '',
          firstName: oldUserInfo.firstName || '',
          lastName: oldUserInfo.lastName || '',
          idCard: requestData.user.cid || '',
          dateOfBirth: oldUserInfo.dateOfBirth || '',
          nationality: oldUserInfo.nationality || '',
          phoneNumber: oldUserInfo.phoneNumber || '',
          lineId: oldUserInfo.lineId || '',
          email: requestData.user.email || '',

          // Current Address
          currentHouseNumber: oldUserInfo.currentAddress?.houseNumber || oldUserInfo.currentAddress || '',
          currentVillage: oldUserInfo.currentAddress?.village || '',
          currentSoi: oldUserInfo.currentAddress?.soi || '',
          currentRoad: oldUserInfo.currentAddress?.road || '',
          currentSubdistrict: oldUserInfo.currentAddress?.subdistrict || oldUserInfo.currentSubdistrict || '',
          currentDistrict: oldUserInfo.currentAddress?.district || oldUserInfo.currentDistrict || '',
          currentProvince: oldUserInfo.currentAddress?.province || oldUserInfo.currentProvince || '',
          currentPostalCode: oldUserInfo.currentAddress?.postalCode || oldUserInfo.currentPostalCode || '',

          // Registered Address
          registeredHouseNumber: oldUserInfo.registeredAddress?.houseNumber || oldUserInfo.address || '',
          registeredVillage: oldUserInfo.registeredAddress?.village || '',
          registeredSoi: oldUserInfo.registeredAddress?.soi || '',
          registeredRoad: oldUserInfo.registeredAddress?.road || '',
          registeredSubdistrict: oldUserInfo.registeredAddress?.subdistrict || oldUserInfo.subdistrict || '',
          registeredDistrict: oldUserInfo.registeredAddress?.district || oldUserInfo.district || '',
          registeredProvince: oldUserInfo.registeredAddress?.province || oldUserInfo.province || '',
          registeredPostalCode: oldUserInfo.registeredAddress?.postalCode || oldUserInfo.postalCode || ''
        }, { emitEvent: false });

        console.log('✅ Form populated successfully with user information from request data (fallback)');
      } else {
        console.warn('⚠️ User information not found in response, only request data populated');
      }
    } catch (error) {
      console.error('❌ Error populating form:', error);
      // Don't throw error, just log it - form will show with partial data
    }
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps && step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    if (this.currentStep === 1) {
      // Step 1 (Personal Information) is read-only, always valid
      return true;
    }

    if (this.currentStep === 2) {
      // Validate status field in Step 2 (Review and Confirm)
      const statusControl = this.renewalForm.get('status');
      const isValid = statusControl ? statusControl.valid : false;

      if (!isValid) {
        alert('กรุณาเลือกสถานะคำขอ');
      }

      return isValid;
    }

    if (this.currentStep === 3) {
      // Validate payment status field in Step 3
      const paymentStatusControl = this.renewalForm.get('paymentStatus');
      const isValid = paymentStatusControl ? paymentStatusControl.valid : false;

      if (!isValid) {
        alert('กรุณาเลือกสถานะการชำระเงิน');
      }

      return isValid;
    }

    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string, errorType?: string): boolean {
    const control = this.renewalForm.get(fieldName);
    if (!control) return false;

    if (errorType) {
      return control.hasError(errorType) && (control.dirty || control.touched);
    }

    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.renewalForm.get(fieldName);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'กรุณากรอกข้อมูลนี้';
    if (control.errors['min']) return `ค่าต้องไม่น้อยกว่า ${control.errors['min'].min}`;

    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Submit form (Update renewal request)
   */
  onSubmit(): void {
    if (this.renewalForm.valid) {
      this.isSubmitting = true;

      const requestId = this.renewalForm.get('requestId')?.value;

      if (!requestId) {
        alert('ไม่พบข้อมูลคำขอ');
        this.isSubmitting = false;
        return;
      }

      // Prepare update data for renewal request
      const updateData: any = {
        workExperienceYears: this.renewalForm.get('workExperienceYears')?.value,
        feeAmount: this.renewalForm.get('feeAmount')?.value,
        status: this.renewalForm.get('status')?.value
      };

      // Prepare VAT member data for user-information
      const vatMemberData: any = {
        vatId: this.renewalForm.get('cardNumber')?.value,
        // ต่ออายุตลอดชีพ: ไม่มีวันหมดอายุ และอัปเดตประเภทสมาชิกเป็นตลอดชีพ
        vatExpired: this.isLifetimeRenewal
          ? null
          : this.renewalForm.get('cardExpiryDate')?.value
      };

      if (this.isLifetimeRenewal) {
        vatMemberData.vatType = LIFETIME_MEMBER_LABEL;
      }

      // Send PATCH request to update renewal request
      const requestApiUrl = `${environment.apiUrl}/form-renew-member/${requestId}`;

      console.log('📤 Updating renewal request:', updateData);

      this.http.patch(requestApiUrl, updateData).subscribe({
        next: (response: any) => {
          console.log('✅ Renewal request updated successfully:', response);

          // If userId exists, update user-information with VAT member data
          if (this.userId) {
            this.updateUserInformation(vatMemberData);
          } else {
            this.isSubmitting = false;
            alert('บันทึกข้อมูลสำเร็จ');
            this.router.navigate(['/staff/request-renew-member-management']);
          }
        },
        error: (error) => {
          console.error('❌ Error updating renewal request:', error);
          this.isSubmitting = false;

          const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง';
          alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
        }
      });
    } else {
      Object.keys(this.renewalForm.controls).forEach(key => {
        this.renewalForm.get(key)?.markAsTouched();
      });

      alert('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง');
    }
  }

  /**
   * Update user information with VAT member data
   */
  private updateUserInformation(vatMemberData: any): void {
    const userApiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;

    console.log('📤 Updating user information:', vatMemberData);

    this.http.patch(userApiUrl, vatMemberData).subscribe({
      next: (response: any) => {
        console.log('✅ User information updated successfully:', response);
        this.isSubmitting = false;

        alert('บันทึกข้อมูลสำเร็จ');

        // Navigate back to list
        this.router.navigate(['/staff/request-renew-member-management']);
      },
      error: (error) => {
        console.error('❌ Error updating user information:', error);
        this.isSubmitting = false;

        const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูลสมาชิก VAT';
        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลส่วนตัว';
      case 2: return 'ยืนยันและบันทึก';
      case 3: return 'สถานะการชำระเงิน';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-person';
      case 2: return 'bi-check-circle';
      case 3: return 'bi-credit-card';
      default: return 'bi-circle';
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  /**
   * Check if step is current
   */
  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Go back to list
   */
  goBack(): void {
    if (confirm('คุณต้องการออกจากหน้านี้หรือไม่? การเปลี่ยนแปลงที่ยังไม่ได้บันทึกจะหายไป')) {
      this.router.navigate(['/staff/request-renew-member-management']);
    }
  }

  /**
   * Get status label
   */
  getStatusLabel(): string {
    const value = this.renewalForm.get('status')?.value;
    return value || '-';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(): string {
    const value = this.renewalForm.get('status')?.value;
    // Map status values to badge classes
    const statusClassMap: { [key: string]: string } = {
      'รอตรวจเอกสาร': 'bg-secondary',
      'ผ่านการตรวจสอบ': 'bg-info',
      'ดำเนินการออกบัตร': 'bg-primary',
      'ดำเนินการเสร็จสิ้น': 'bg-success',
      'ยกเลิก': 'bg-danger'
    };
    return statusClassMap[value] || 'bg-secondary';
  }

  /**
   * Get full address string
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const parts = [
      this.renewalForm.get(`${prefix}HouseNumber`)?.value,
      this.renewalForm.get(`${prefix}Village`)?.value,
      this.renewalForm.get(`${prefix}Soi`)?.value,
      this.renewalForm.get(`${prefix}Road`)?.value,
      this.renewalForm.get(`${prefix}Subdistrict`)?.value,
      this.renewalForm.get(`${prefix}District`)?.value,
      this.renewalForm.get(`${prefix}Province`)?.value,
      this.renewalForm.get(`${prefix}PostalCode`)?.value
    ].filter(part => part && part.trim() !== '');

    return parts.join(' ') || '-';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Get payment status label
   */
  getPaymentStatusLabel(): string {
    const value = this.renewalForm.get('paymentStatus')?.value;
    return value || '-';
  }

  /**
   * Get payment status badge class
   */
  getPaymentStatusBadgeClass(): string {
    const value = this.renewalForm.get('paymentStatus')?.value;
    // Map payment status values to badge classes
    const paymentStatusClassMap: { [key: string]: string } = {
      '-': 'bg-secondary',
      'รอชำระเงิน': 'bg-warning',
      'ชำระเงินเสร็จสิ้น': 'bg-success',
      'ไม่ผ่านการตรวจสอบ': 'bg-danger',
      'รอตรวจสอบยอดชำระ': 'bg-info',
      'ยกเลิกการชำระ': 'bg-danger'
    };
    return paymentStatusClassMap[value] || 'bg-secondary';
  }

  /**
   * Get valuation experience array (read-only)
   */
  get valuationExperienceList(): any[] {
    return this.valuationExperience;
  }

  /**
   * Get education background array (read-only)
   */
  get educationBackgroundList(): any[] {
    return this.educationHistory;
  }

  /**
   * Get special courses array (read-only)
   */
  get specialCoursesList(): any[] {
    return this.specialCourses;
  }

  /**
   * Get VAT courses array (read-only) - Now from CPD Hours API
   */
  get vatCoursesList(): any[] {
    return this.cpdHoursData || [];
  }

  /**
   * Calculate total CPD hours from verified courses only
   */
  calculateTotalCpdHours(): number {
    if (!this.cpdHoursData || this.cpdHoursData.length === 0) {
      return 0;
    }

    return this.cpdHoursData.reduce((total, course) => {
      // Only count hours if isVerified is true
      if (course.isVerified === true) {
        const hours = parseFloat(course.course?.duration || course.cpdHours || 0);
        return total + hours;
      }
      return total;
    }, 0);
  }

  /**
   * Check if file is an image
   */
  isImageFile(fileName: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  /**
   * Check if file is a PDF
   */
  isPdfFile(fileName: string): boolean {
    return fileName.toLowerCase().endsWith('.pdf');
  }

  /**
   * Get file icon class based on file type
   */
  getFileIcon(fileName: string): string {
    if (this.isImageFile(fileName)) {
      return 'bi-file-earmark-image';
    } else if (this.isPdfFile(fileName)) {
      return 'bi-file-earmark-pdf';
    }
    return 'bi-file-earmark';
  }

  /**
   * Open document in new tab
   */
  openDocument(fileUrl: string): void {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }

  /**
   * Check if payment slip is available
   */
  hasPaymentSlip(): boolean {
    return !!this.paymentSlip && this.paymentSlip.trim() !== '';
  }

  /**
   * Open payment slip preview in modal or new tab
   */
  openPaymentSlipPreview(): void {
    if (this.hasPaymentSlip()) {
      window.open(this.paymentSlip, '_blank');
    }
  }

  /**
   * สถานะการชำระเงินของคำขอนี้ (แสดงอย่างเดียว)
   *
   * การปรับสถานะและการออกใบเสร็จเป็นหน้าที่ของฝ่ายการเงิน ทำที่เมนู
   * "ตรวจสอบการชำระเงิน" (/staff/payment-verification) ไม่ใช่ที่หน้าใบคำขอนี้
   */
  get paymentStatusDisplay(): string {
    return this.renewalForm.get('paymentStatus')?.value || '-';
  }
}
